import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import { formatGameTime } from '../utils/formatters';
import { useBooks } from '../contexts/BooksContext';
import { isExcludedFromBestBook } from '../utils/constants';

const BestBetsTakeover = ({ show, onHide, bestBets, getTeamLogoUrl, selectedSport }) => {
  const { bestBetsMaxMobile, isBookEnabled } = useBooks();
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [showFilters, setShowFilters] = useState(false);
  const [selectedMarket, setSelectedMarket] = useState('all');
  const [selectedPoint, setSelectedPoint] = useState('all');
  const [selectedBook, setSelectedBook] = useState('all');
  const [selectedGame, setSelectedGame] = useState('all');
  const [selectedConference, setSelectedConference] = useState('all');
  const [minOdds, setMinOdds] = useState(null);
  const [maxOdds, setMaxOdds] = useState(null);
  // Tier filter state - which tiers are enabled (green=edge-one, orange=edge-two, blue=edge-three)
  const [enabledTiers, setEnabledTiers] = useState({
    'edge-one': true,   // Green (≥25%)
    'edge-two': true,   // Orange (≥15%)
    'edge-three': true  // Blue (≥5%)
  });
  // Hide bet365 toggle for NCAAB (due to inconsistent alt lines)
  const [hideBet365, setHideBet365] = useState(false);
  const [showBet365Tooltip, setShowBet365Tooltip] = useState(false);
  const hasInitialized = useRef(false);

  // Market type options for the dropdown (only for ML best bets)
  const marketTypes = [
    { value: 'all', label: 'All' },
    { value: 'moneyline', label: 'Moneyline' },
    { value: 'spreads', label: 'Spreads' },
    { value: 'totals', label: 'Totals' },
    { value: 'halves', label: '1st Half' },
    { value: 'quarters', label: '1st Qtr' }
  ];

  // Helper to check if a bet matches a market type (stable function)
  const betMatchesMarketType = useCallback((bet, marketType) => {
    if (marketType === 'all') return true;
    const market = bet.market?.toLowerCase() || '';
    if (marketType === 'moneyline') {
      return market === 'moneyline' || market === 'h2h';
    }
    if (marketType === 'spreads') {
      return market === 'spread' || market === 'spreads' || market === 'alt spread';
    }
    if (marketType === 'totals') {
      return market === 'totals' || market === 'total' || market === 'alt total';
    }
    if (marketType === 'halves') {
      return market === '1st half ml' || market.includes('half');
    }
    if (marketType === 'quarters') {
      return market === '1st qtr ml' || market.includes('qtr') || market.includes('quarter');
    }
    return true;
  }, []);

  // Parse odds string to number (e.g., "+150" -> 150, "-110" -> -110)
  const parseOdds = useCallback((oddsStr) => {
    if (!oddsStr) return null;
    return parseInt(oddsStr.replace('+', ''), 10);
  }, []);

  // =============================================================================
  // MEMOIZED COMPUTATIONS - only recalculate when bestBets changes
  // =============================================================================

  // Check if this is ML best bets (has mainline market types)
  const isMainlineBestBets = useMemo(() => bestBets.some(bet => {
    const market = bet.market?.toLowerCase() || '';
    return market === 'moneyline' || market === 'h2h' ||
           market === 'spread' || market === 'spreads' || market === 'alt spread' ||
           market === 'totals' || market === 'total' || market === 'alt total' ||
           market === '1st half ml' || market === '1st qtr ml';
  }), [bestBets]);

  // Get odds range from bestBets
  const { oddsMin, oddsMax } = useMemo(() => {
    const oddsValues = bestBets
      .map(bet => parseOdds(bet.odds))
      .filter(odds => odds !== null && !isNaN(odds));
    return {
      oddsMin: oddsValues.length > 0 ? Math.min(...oddsValues) : -200,
      oddsMax: oddsValues.length > 0 ? Math.max(...oddsValues) : 300
    };
  }, [bestBets, parseOdds]);

  // Reset filters when takeover closes, initialize only once when it opens
  useEffect(() => {
    if (show && bestBets.length > 0 && !hasInitialized.current) {
      // Initialize odds to data range when takeover first opens
      setMinOdds(oddsMin);
      setMaxOdds(oddsMax);
      hasInitialized.current = true;
    } else if (!show) {
      // Reset all filters when takeover closes
      setSelectedMarket('all');
      setSelectedPoint('all');
      setSelectedBook('all');
      setSelectedGame('all');
      setSelectedConference('all');
      setMinOdds(null);
      setMaxOdds(null);
      setEnabledTiers({ 'edge-one': true, 'edge-two': true, 'edge-three': true });
      setHideBet365(false);
      setShowFilters(false);
      hasInitialized.current = false;
    }
  }, [show, bestBets.length, oddsMin, oddsMax]);

  // Reset line filter when market changes (since lines are market-specific)
  useEffect(() => {
    setSelectedPoint('all');
  }, [selectedMarket]);

  // Get unique point values from bestBets, filtered by selected market, sorted numerically
  const uniquePoints = useMemo(() => [...new Set(
    bestBets
      .filter(bet => bet.point !== null && bet.point !== undefined)
      .filter(bet => betMatchesMarketType(bet, selectedMarket))
      .map(bet => bet.point)
  )].sort((a, b) => a - b), [bestBets, selectedMarket, betMatchesMarketType]);

  // Get unique books from bestBets (the "best" book for each bet) and count per book.
  // Also include Secondary books that appear in any bet's secondaryMatches — that
  // way Kalshi etc. become selectable filter options when they have active edges.
  const { bookCounts, uniqueBooks } = useMemo(() => {
    const counts = {};
    bestBets.forEach(bet => {
      if (bet.book) {
        const book = bet.book.toLowerCase();
        counts[book] = (counts[book] || 0) + 1;
      }
      if (Array.isArray(bet.secondaryMatches)) {
        bet.secondaryMatches.forEach(s => {
          const sBook = String(s.book).toLowerCase();
          // Skip books hidden via the context — only visible Secondaries should
          // appear in the filter dropdown.
          if (!isBookEnabled(sBook)) return;
          counts[sBook] = (counts[sBook] || 0) + 1;
        });
      }
    });
    return { bookCounts: counts, uniqueBooks: Object.keys(counts).sort() };
  }, [bestBets, isBookEnabled]);

  // Get unique games from bestBets
  const uniqueGames = useMemo(() => [...new Set(
    bestBets
      .filter(bet => bet.game)
      .map(bet => bet.game)
  )].sort(), [bestBets]);

  // Get unique conferences from bestBets (for NCAAB)
  const uniqueConferences = useMemo(() => [...new Set(
    bestBets
      .flatMap(bet => [bet.homeConference, bet.awayConference])
      .filter(conf => conf)
  )].sort(), [bestBets]);

  // Helper to check if a bet involves a Top 25 team
  const betInvolvesTop25 = useCallback((bet) => {
    return (bet.homeRank && bet.homeRank <= 25) || (bet.awayRank && bet.awayRank <= 25);
  }, []);

  // Helper to check if a bet matches the selected conference
  const betMatchesConference = useCallback((bet, conference) => {
    if (conference === 'all') return true;
    if (conference === 'top25') return (bet.homeRank && bet.homeRank <= 25) || (bet.awayRank && bet.awayRank <= 25);
    return bet.homeConference === conference || bet.awayConference === conference;
  }, []);

  // Helper to toggle a tier on/off
  const toggleTier = useCallback((tier) => {
    setEnabledTiers(prev => ({ ...prev, [tier]: !prev[tier] }));
  }, []);

  // Check if any tier filter is active (not all enabled)
  const tierFilterActive = !enabledTiers['edge-one'] || !enabledTiers['edge-two'] || !enabledTiers['edge-three'];

  // Filter bestBets based on selected filters (memoized)
  const filteredBets = useMemo(() => bestBets.filter(bet => {
    const matchesMarket = betMatchesMarketType(bet, selectedMarket);
    const matchesConference = betMatchesConference(bet, selectedConference);
    const matchesPoint = selectedPoint === 'all' || bet.point === parseFloat(selectedPoint);
    const matchesBook = selectedBook === 'all'
      || bet.book.toLowerCase() === selectedBook
      || (bet.secondaryMatches && bet.secondaryMatches.some(s => String(s.book).toLowerCase() === selectedBook));
    const matchesGame = selectedGame === 'all' || bet.game === selectedGame;
    const betOdds = parseOdds(bet.odds);
    const matchesMinOdds = minOdds === null || betOdds === null || betOdds >= minOdds;
    const matchesMaxOdds = maxOdds === null || betOdds === null || betOdds <= maxOdds;
    // Tier filter - check if bet's tier is enabled
    const matchesTier = bet.tier ? enabledTiers[bet.tier] : true;
    // Hide bet365 filter for NCAAB
    const matchesBet365Filter = !hideBet365 || bet.book.toLowerCase() !== 'bet365';
    return matchesMarket && matchesConference && matchesPoint && matchesBook && matchesGame && matchesMinOdds && matchesMaxOdds && matchesTier && matchesBet365Filter;
  }), [bestBets, betMatchesMarketType, selectedMarket, betMatchesConference, selectedConference, selectedPoint, selectedBook, selectedGame, parseOdds, minOdds, maxOdds, enabledTiers, hideBet365]);

  const oddsFilterActive = minOdds !== null && maxOdds !== null && (minOdds !== oddsMin || maxOdds !== oddsMax);
  const isFiltered = selectedMarket !== 'all' || selectedPoint !== 'all' || selectedBook !== 'all' || selectedGame !== 'all' || selectedConference !== 'all' || oddsFilterActive || tierFilterActive || hideBet365;

  // Apply max results limit from settings (mobile) - memoized
  const displayedBets = useMemo(() => bestBetsMaxMobile !== null
    ? filteredBets.slice(0, bestBetsMaxMobile)
    : filteredBets, [filteredBets, bestBetsMaxMobile]);
  const isLimited = bestBetsMaxMobile !== null && filteredBets.length > bestBetsMaxMobile;

  const activeFilterCount = [
    selectedMarket !== 'all',
    selectedPoint !== 'all',
    selectedBook !== 'all',
    selectedConference !== 'all',
    selectedGame !== 'all',
    oddsFilterActive,
    tierFilterActive,
    hideBet365
  ].filter(Boolean).length;

  // Helper function to get the next best odds — eligible books only so
  // Secondary books don't appear in the collapsed-row "vs" tile.
  const getNextBestOdds = useCallback((bet) => {
    const bestBookKey = bet.book.toLowerCase();
    // Filter out _isAvailable keys - only get actual book names
    const allBooks = Object.keys(bet.allOdds || {}).filter(key => !key.includes('_isAvailable'));

    const otherOdds = allBooks
      .filter(book =>
        book !== bestBookKey &&
        isBookEnabled(book) &&
        !isExcludedFromBestBook(book) &&
        bet.allOdds[book] !== null &&
        bet.allOdds[book] !== undefined
      )
      .map(book => ({ book, odds: bet.allOdds[book] }))
      .sort((a, b) => b.odds - a.odds);

    return otherOdds.length > 0 ? otherOdds[0] : null;
  }, [isBookEnabled]);

  // Helper function to get the next best 4 books (excluding the book currently
  // shown as "best" and any other Secondary/hidden books). Secondaries get
  // their own row below. When the user filters by a Secondary book and we swap
  // it into the "best" slot, pass that Secondary as displayBestKey so the
  // original primary correctly appears in the 4-tile row.
  const getNextBestBooks = useCallback((bet, displayBestKey) => {
    const excludeKey = (displayBestKey || bet.book).toLowerCase();
    // Filter out _isAvailable keys - only get actual book names
    const allBooks = Object.keys(bet.allOdds || {}).filter(key => !key.includes('_isAvailable'));

    const otherBooks = allBooks
      .filter(book => book !== excludeKey && isBookEnabled(book) && !isExcludedFromBestBook(book))
      .map(book => ({ book, odds: bet.allOdds[book] }))
      .sort((a, b) => {
        if ((a.odds === null || a.odds === undefined) && (b.odds === null || b.odds === undefined)) return 0;
        if (a.odds === null || a.odds === undefined) return 1;
        if (b.odds === null || b.odds === undefined) return -1;
        return b.odds - a.odds;
      })
      .slice(0, 4);

    return otherBooks;
  }, [isBookEnabled]);

  // Secondary books (Kalshi, Circa, …) whose price ties or beats the primary
  // best on this row — surfaced below the 4-tile row as a separate section.
  const getActiveSecondaryBooks = useCallback((bet) => {
    const bestBookKey = bet.book.toLowerCase();
    const primaryOdds = bet.allOdds[bestBookKey];
    if (primaryOdds === null || primaryOdds === undefined) return [];
    const allBooks = Object.keys(bet.allOdds || {}).filter(key => !key.includes('_isAvailable'));
    return allBooks
      .filter(book => book !== bestBookKey && isBookEnabled(book) && isExcludedFromBestBook(book))
      .map(book => ({ book, odds: bet.allOdds[book] }))
      .filter(({ odds }) => {
        if (odds === null || odds === undefined) return false;
        return (
          (odds > 0 && primaryOdds > 0 && odds >= primaryOdds) ||
          (odds < 0 && primaryOdds < 0 && odds >= primaryOdds)
        );
      })
      .sort((a, b) => b.odds - a.odds);
  }, [isBookEnabled]);

  const toggleExpanded = useCallback((index) => {
    setExpandedItems(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(index)) {
        newExpanded.delete(index);
      } else {
        newExpanded.add(index);
      }
      return newExpanded;
    });
  }, []);

  const clearFilters = useCallback(() => {
    setSelectedMarket('all');
    setSelectedPoint('all');
    setSelectedBook('all');
    setSelectedGame('all');
    setSelectedConference('all');
    setMinOdds(oddsMin);
    setMaxOdds(oddsMax);
    setEnabledTiers({ 'edge-one': true, 'edge-two': true, 'edge-three': true });
    setHideBet365(false);
  }, [oddsMin, oddsMax]);

  if (!show) return null;

  return (
    <div className="best-bets-takeover">
      <div className="best-bets-takeover-header">
        <h5 className="mb-0">Best Bets Right Now</h5>
        <div className="d-flex align-items-center gap-2">
          <Button
            variant={showFilters ? 'light' : 'outline-light'}
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className="d-flex align-items-center gap-1"
          >
            <span>Filters</span>
            {activeFilterCount > 0 && (
              <Badge bg="danger" pill style={{ fontSize: '0.65rem' }}>
                {activeFilterCount}
              </Badge>
            )}
          </Button>
          <Button
            variant="link"
            onClick={onHide}
            className="text-white p-0 ms-2"
            style={{ fontSize: '1.5rem', lineHeight: 1 }}
          >
            ×
          </Button>
        </div>
      </div>

      {/* Filter Panel */}
      <div className={`best-bets-filter-panel ${showFilters ? 'open' : ''}`}>
        <div className="filter-panel-content">
          <div className="mobile-filter-row">
            {isMainlineBestBets && (
              <div className="mobile-filter-group">
                <label>Market</label>
                <select
                  value={selectedMarket}
                  onChange={(e) => setSelectedMarket(e.target.value)}
                >
                  {marketTypes.map(mt => (
                    <option key={mt.value} value={mt.value}>{mt.label}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="mobile-filter-group">
              <label>Line</label>
              <select
                value={selectedPoint}
                onChange={(e) => setSelectedPoint(e.target.value)}
              >
                <option value="all">All</option>
                {uniquePoints.map(point => (
                  <option key={point} value={point}>{point}</option>
                ))}
              </select>
            </div>
            <div className="mobile-filter-group">
              <label>Book</label>
              <select
                value={selectedBook}
                onChange={(e) => setSelectedBook(e.target.value)}
              >
                <option value="all">All ({bestBets.filter(bet => bet.book).length})</option>
                {uniqueBooks.map(book => (
                  <option key={book} value={book}>
                    {book.charAt(0).toUpperCase() + book.slice(1)} ({bookCounts[book]})
                  </option>
                ))}
              </select>
            </div>
            {(selectedSport === 'ncaab' || selectedSport === 'ncaaf') && uniqueConferences.length > 0 && (
              <div className="mobile-filter-group">
                <label>Conf</label>
                <select
                  value={selectedConference}
                  onChange={(e) => setSelectedConference(e.target.value)}
                >
                  <option value="all">All</option>
                  <option value="top25">Top 25</option>
                  {uniqueConferences.map(conf => (
                    <option key={conf} value={conf}>{conf}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="mobile-filter-group">
              <label>Game</label>
              <select
                value={selectedGame}
                onChange={(e) => setSelectedGame(e.target.value)}
              >
                <option value="all">All</option>
                {uniqueGames.map(game => (
                  <option key={game} value={game}>{game}</option>
                ))}
              </select>
            </div>
          </div>
          {/* Tier Filter Icons */}
          <div className="mobile-filter-row" style={{ justifyContent: 'flex-start', gap: '12px', paddingLeft: '4px' }}>
            <label style={{ marginRight: '4px', fontSize: '0.85rem' }}>Tiers:</label>
            <div
              onClick={() => toggleTier('edge-one')}
              style={{
                width: '28px',
                height: '28px',
                borderRadius: '4px',
                backgroundColor: enabledTiers['edge-one'] ? 'green' : '#333',
                border: enabledTiers['edge-one'] ? '2px solid #4ade80' : '2px solid #555',
                cursor: 'pointer',
                opacity: enabledTiers['edge-one'] ? 1 : 0.4,
                transition: 'all 0.2s ease'
              }}
            />
            <div
              onClick={() => toggleTier('edge-two')}
              style={{
                width: '28px',
                height: '28px',
                borderRadius: '4px',
                backgroundColor: enabledTiers['edge-two'] ? '#F59E0B' : '#333',
                border: enabledTiers['edge-two'] ? '2px solid #fbbf24' : '2px solid #555',
                cursor: 'pointer',
                opacity: enabledTiers['edge-two'] ? 1 : 0.4,
                transition: 'all 0.2s ease'
              }}
            />
            <div
              onClick={() => toggleTier('edge-three')}
              style={{
                width: '28px',
                height: '28px',
                borderRadius: '4px',
                backgroundColor: enabledTiers['edge-three'] ? '#3B82F6' : '#333',
                border: enabledTiers['edge-three'] ? '2px solid #60a5fa' : '2px solid #555',
                cursor: 'pointer',
                opacity: enabledTiers['edge-three'] ? 1 : 0.4,
                transition: 'all 0.2s ease'
              }}
            />
          </div>
          {/* Hide Bet365 toggle - NCAAB only */}
          {selectedSport === 'ncaab' && (
            <div className="mobile-filter-row" style={{ justifyContent: 'flex-start', gap: '12px', paddingLeft: '4px', position: 'relative' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', margin: 0, fontSize: '0.85rem' }}>
                <input
                  type="checkbox"
                  checked={hideBet365}
                  onChange={(e) => setHideBet365(e.target.checked)}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span>Hide Bet365</span>
              </label>
              <div
                style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  backgroundColor: '#4b5563',
                  color: '#d1d5db',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
                onClick={() => setShowBet365Tooltip(!showBet365Tooltip)}
              >
                ?
              </div>
              {showBet365Tooltip && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: '10px',
                  marginTop: '8px',
                  padding: '10px 12px',
                  backgroundColor: '#1f2937',
                  border: '1px solid #374151',
                  borderRadius: '6px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                  zIndex: 1000,
                  width: '260px',
                  fontSize: '13px',
                  color: '#e5e7eb',
                  lineHeight: '1.4'
                }}>
                  NCAAB alt lines for Bet365 have been inconsistent. To exclude them from Best Bets, click the toggle.
                  <div
                    onClick={() => setShowBet365Tooltip(false)}
                    style={{ marginTop: '8px', textAlign: 'right', color: '#9ca3af', fontSize: '12px', cursor: 'pointer' }}
                  >
                    Tap to close
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="mobile-filter-row">
            <div className="mobile-filter-group slider-group">
              <label>Min Odds</label>
              <div className="slider-with-value">
                <input
                  type="range"
                  min={oddsMin}
                  max={oddsMax}
                  value={minOdds ?? oddsMin}
                  onChange={(e) => setMinOdds(parseInt(e.target.value, 10))}
                />
                <input
                  type="number"
                  className="odds-input"
                  value={minOdds ?? oddsMin}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10);
                    if (!isNaN(val)) setMinOdds(val);
                  }}
                />
              </div>
            </div>
            <div className="mobile-filter-group slider-group">
              <label>Max Odds</label>
              <div className="slider-with-value">
                <input
                  type="range"
                  min={oddsMin}
                  max={oddsMax}
                  value={maxOdds ?? oddsMax}
                  onChange={(e) => setMaxOdds(parseInt(e.target.value, 10))}
                />
                <input
                  type="number"
                  className="odds-input"
                  value={maxOdds ?? oddsMax}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10);
                    if (!isNaN(val)) setMaxOdds(val);
                  }}
                />
              </div>
            </div>
          </div>
          <div className="mobile-filter-row filter-actions">
            {isFiltered && (
              <div className="filter-status">
                Showing {displayedBets.length}{isLimited ? ` (max ${bestBetsMaxMobile})` : ''} of {filteredBets.length}
              </div>
            )}
            {isFiltered && (
              <Button variant="outline-secondary" size="sm" onClick={clearFilters}>
                Clear Filters
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="best-bets-takeover-body">
        {bestBets.length === 0 ? (
          <div className="text-center py-5">
            <p className="text-muted">No best bets available right now.</p>
            <small>Best bets are highlighted when one book has significantly better odds.</small>
          </div>
        ) : displayedBets.length === 0 ? (
          <div className="text-center py-5">
            <p className="text-muted">No bets match your filters.</p>
            <Button variant="outline-primary" size="sm" onClick={clearFilters}>
              Clear Filters
            </Button>
          </div>
        ) : (
          <div className="best-bets-mobile-list">
            {displayedBets.map((bet, index) => {
              const primaryBookKey = bet.book.toLowerCase();
              // When the user filters by a Secondary book that's active on this
              // row, swap the displayed "best" to that Secondary so the user
              // sees the price they actually want — with the original primary
              // shown as the next-best beside it.
              const secondaryMatch = (
                selectedBook !== 'all' &&
                isExcludedFromBestBook(selectedBook) &&
                Array.isArray(bet.secondaryMatches) &&
                bet.secondaryMatches.find(s => String(s.book).toLowerCase() === selectedBook)
              );
              const bestBookKey = secondaryMatch ? selectedBook : primaryBookKey;
              const nextBest = secondaryMatch
                ? { book: primaryBookKey, odds: bet.allOdds[primaryBookKey] }
                : getNextBestOdds(bet);
              const isExpanded = expandedItems.has(index);

              return (
                <div key={index}>
                  <div
                    className={`best-bet-list-item tier-${bet.tier} ${isExpanded ? 'expanded' : ''}`}
                    onClick={() => toggleExpanded(index)}
                    style={{
                      cursor: 'pointer',
                      position: 'relative',
                      // Caret in the top-right corner when a Secondary book
                      // (e.g. Kalshi, Circa) also matches or beats this price.
                      ...(bet.secondaryMatches && bet.secondaryMatches.length > 0 ? {
                        backgroundImage: 'linear-gradient(225deg, #9CA3AF 8px, transparent 8px)',
                        backgroundRepeat: 'no-repeat',
                      } : {}),
                    }}
                    title={bet.secondaryMatches && bet.secondaryMatches.length > 0
                      ? `Also at ${bet.secondaryMatches.map(s => `${s.book} ${s.price > 0 ? '+' : ''}${s.price}`).join(', ')}`
                      : undefined}
                  >
                    <div className="best-bet-list-left">
                      <div className="best-bet-list-game">
                        {bet.game} • {formatGameTime(bet.gameTime)}
                      </div>
                      <div className="best-bet-list-player">
                        {bet.team && (
                          <img
                            src={bet.teamLogo || getTeamLogoUrl(bet.team)}
                            alt={bet.team}
                            className="best-bets-team-logo-small"
                          />
                        )}
                        {bet.market === 'Totals' && (bet.betType === 'Over' || bet.betType === 'Under') && (
                          <div style={{
                            flexShrink: 0,
                            width: '20px',
                            height: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '14px',
                            color: bet.betType === 'Over' ? '#10B981' : '#EF4444',
                            marginRight: '8px'
                          }}>
                            {bet.betType === 'Over' ? '↑' : '↓'}
                          </div>
                        )}
                        <div>
                          <span>
                            {bet.starter ? `${bet.player}*` : bet.player}
                            {bet.battingOrder && (
                              <sup style={{
                                fontSize: '0.6em',
                                color: '#B3B3B3',
                                fontWeight: 'normal',
                                lineHeight: '1',
                                marginLeft: '2px'
                              }}>
                                {bet.battingOrder}
                              </sup>
                            )}
                            {bet.position && (
                              <span style={{
                                fontSize: '0.75rem',
                                color: '#9CA3AF',
                                fontWeight: 'normal',
                                marginLeft: '4px'
                              }}>
                                ({bet.position})
                              </span>
                            )}
                          </span>
                          {bet.awayPitcher && bet.homePitcher && bet.awayPitcher !== 'TBD' && bet.homePitcher !== 'TBD' && (
                            <div style={{
                              fontSize: '0.75rem',
                              color: '#9CA3AF',
                              marginTop: '2px'
                            }}>
                              {bet.awayPitcher.split(' ').pop()} vs {bet.homePitcher.split(' ').pop()}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="best-bet-list-right">
                      <div className="best-bet-list-odds">
                        <div className="best-odds" style={bet.allOdds[`${bestBookKey}_isAvailable`] === false ? { opacity: 0.25 } : undefined}>
                          <img src={`/logos/${bestBookKey}.png`} alt={bet.book} />
                          <span className="odds-value">{bet.allOdds[bestBookKey] > 0 ? '+' : ''}{bet.allOdds[bestBookKey]}</span>
                        </div>
                        {nextBest && (() => {
                          // Calculate proper tier for next best when bet365 is best (only for NCAAB)
                          let nextBestTierClass = '';
                          if (bestBookKey === 'bet365' && selectedSport === 'ncaab') {
                            const allBooks = Object.keys(bet.allOdds || {}).filter(key => !key.includes('_isAvailable'));
                            const otherOdds = allBooks
                              .filter(book => book !== 'bet365' && bet.allOdds[book] !== null && bet.allOdds[book] !== undefined)
                              .map(book => ({ book, odds: bet.allOdds[book] }))
                              .sort((a, b) => b.odds - a.odds);

                            if (otherOdds.length >= 2) {
                              const nextBestOdds = otherOdds[0].odds;  // 2nd best (after bet365)
                              const thirdBestOdds = otherOdds[1].odds; // 3rd best

                              // Calculate diff using proper American odds formula
                              let diff;
                              if ((nextBestOdds > 0 && thirdBestOdds > 0) || (nextBestOdds < 0 && thirdBestOdds < 0)) {
                                // Both same sign
                                diff = Math.abs((nextBestOdds - thirdBestOdds) / thirdBestOdds);
                              } else if (nextBestOdds > 0 && thirdBestOdds < 0) {
                                // Next best is positive, third best is negative (crossing even money)
                                const nextDistance = nextBestOdds - 100;
                                const thirdDistance = thirdBestOdds + 100;
                                diff = Math.abs((nextDistance - thirdDistance) / thirdBestOdds);
                              } else {
                                // Next best is negative, third best is positive
                                const nextDistance = nextBestOdds + 100;
                                const thirdDistance = thirdBestOdds - 100;
                                diff = Math.abs((nextDistance - thirdDistance) / thirdBestOdds);
                              }

                              if (diff >= 0.25) {
                                nextBestTierClass = 'tier-edge-one';
                              } else if (diff >= 0.15) {
                                nextBestTierClass = 'tier-edge-two';
                              } else if (diff >= 0.05) {
                                nextBestTierClass = 'tier-edge-three';
                              }
                            }
                          }

                          return (
                            <div
                              className={`next-best-section ${nextBestTierClass}`}
                              style={bet.allOdds[`${nextBest.book}_isAvailable`] === false ? { opacity: 0.25 } : undefined}
                            >
                              <span className="vs-text">vs</span>
                              <img src={`/logos/${nextBest.book}.png`} alt={nextBest.book} />
                              <span className="odds-value">{nextBest.odds > 0 ? '+' : ''}{nextBest.odds}</span>
                            </div>
                          );
                        })()}
                      </div>
                      <span className="expand-indicator">
                        {isExpanded ? '▲' : '▼'}
                      </span>
                      <Badge
                        className={`best-bets-badge tier-${bet.tier}`}
                      >
                        {`${(bet.advantage * 100).toFixed(1)}%`}
                      </Badge>
                      <div className="market-badge-small">
                        <div>{bet.market === 'Doubles' ? '2B' : bet.market === 'Home Runs' ? 'HR' : bet.market === 'Hits' ? 'H' : bet.market === 'RBIs' ? 'RBI' : bet.market === 'Runs' ? 'R' : bet.market === 'Hits + Runs + RBIs' ? 'H+R+R' : bet.market === 'Total Bases' ? 'TB' : bet.market === 'Stolen Bases' ? 'SB' : bet.market === 'Strikeouts' ? 'K' : bet.market === 'Total Touchdowns' ? 'TDs' : bet.market === 'Rush Attempts' ? 'Rush Att' : bet.market === 'Reception Yards' ? 'Rec Yards' : bet.market === 'First Goal Scorer' ? '1st Goal' : bet.market === 'Last Goal Scorer' ? 'Last Goal' : bet.market === 'Shots on Goal' ? 'Shots' : bet.market === 'First Team Basket' ? 'First Team' : bet.market === 'Points + Rebounds + Assists' ? 'P + R + A' : bet.market === 'Points + Rebounds' ? 'P + R' : bet.market === 'Points + Assists' ? 'P + A' : bet.market === 'Rebounds + Assists' ? 'R + A' : (bet.market || '').includes('Touchdown') ? bet.market.replace('Touchdown', 'TD') : bet.market}</div>
                        {bet.point !== null && bet.point !== undefined && (() => {
                          const bt = (bet.betType || '').toLowerCase();
                          const market = (bet.market || '').toLowerCase();
                          // Binary markets (yes/no) - check first before over/under
                          if (bet.point === 'yes' || bet.point === 'no') {
                            return <small style={{ fontSize: '0.7rem', opacity: 0.8 }}>{bet.point === 'yes' ? 'Yes' : 'No'}</small>;
                          }
                          // Totals: show O/U + point
                          if (bt === 'over' || bt === 'under') {
                            return <small style={{ fontSize: '0.7rem', opacity: 0.8 }}>{bt === 'over' ? 'O' : 'U'}{bet.point}</small>;
                          }
                          // Spreads: show +/- point
                          if (bt === 'underdog' || bt === 'favorite' || market.includes('spread')) {
                            const sign = bt === 'underdog' || bt === 'plus' ? '+' : '-';
                            return <small style={{ fontSize: '0.7rem', opacity: 0.8 }}>{sign}{Math.abs(bet.point)}</small>;
                          }
                          // Other markets with numeric points
                          if (typeof bet.point === 'number') {
                            return <small style={{ fontSize: '0.7rem', opacity: 0.8 }}>{bet.point}</small>;
                          }
                          return null;
                        })()}
                      </div>
                    </div>
                  </div>

                  {isExpanded && (() => {
                    const activeSecondaries = getActiveSecondaryBooks(bet);
                    return (
                    <div className="best-bet-expanded">
                      <div className="all-odds-grid">
                        {getNextBestBooks(bet, bestBookKey).map(({ book, odds }) => {
                          const isAvailable = bet.allOdds[`${book}_isAvailable`] !== false;
                          // Primary book's odds for this row (number).
                          const primaryOdds = bet.allOdds[bet.book.toLowerCase()];
                          // Show a corner caret on Secondary book cells whose
                          // odds tie or beat the primary best (same-sign).
                          const isSecondary = isExcludedFromBestBook(book);
                          const tiesOrBeats = isSecondary && odds != null && primaryOdds != null && (
                            (odds > 0 && primaryOdds > 0 && odds >= primaryOdds) ||
                            (odds < 0 && primaryOdds < 0 && odds >= primaryOdds)
                          );
                          let secondaryTooltip;
                          let caretColor;
                          if (tiesOrBeats) {
                            if (odds === primaryOdds) {
                              secondaryTooltip = `${book} ${odds > 0 ? '+' : ''}${odds} — tied with best book`;
                              caretColor = '#9CA3AF'; // tie — neutral grey
                            } else {
                              const edge = Math.abs((odds - primaryOdds) / primaryOdds);
                              const edgePct = edge * 100;
                              secondaryTooltip = `${book} ${odds > 0 ? '+' : ''}${odds} — ${edgePct.toFixed(2)}% edge over best book`;
                              if (edge >= 0.25) caretColor = 'green';
                              else if (edge >= 0.15) caretColor = '#F59E0B';
                              else if (edge >= 0.05) caretColor = '#3B82F6';
                              else caretColor = '#9CA3AF';
                            }
                          }
                          const style = {
                            ...(!isAvailable ? { opacity: 0.25 } : {}),
                            ...(tiesOrBeats ? {
                              backgroundImage: `linear-gradient(225deg, ${caretColor} 10px, transparent 10px)`,
                              backgroundRepeat: 'no-repeat',
                            } : {}),
                          };
                          return (
                            <div
                              key={book}
                              className={`expanded-odds-item ${bet.tier}`}
                              style={Object.keys(style).length > 0 ? style : undefined}
                              title={secondaryTooltip}
                            >
                              <img src={`/logos/${book}.png`} alt={book} />
                              <span>{odds !== null && odds !== undefined ? (odds > 0 ? `+${odds}` : odds) : '-'}</span>
                            </div>
                          );
                        })}
                      </div>
                      {activeSecondaries.length > 0 && (
                        <div style={{ marginTop: '6px', paddingTop: '6px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                          <div style={{ fontSize: '0.65rem', color: '#9CA3AF', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Also at
                          </div>
                          <div className="all-odds-grid">
                            {activeSecondaries.map(({ book, odds }) => {
                              const primaryOdds = bet.allOdds[bet.book.toLowerCase()];
                              let tooltip;
                              let caretColor;
                              if (odds === primaryOdds) {
                                tooltip = `${book} ${odds > 0 ? '+' : ''}${odds} — tied with best book`;
                                caretColor = '#9CA3AF';
                              } else {
                                const edge = Math.abs((odds - primaryOdds) / primaryOdds);
                                tooltip = `${book} ${odds > 0 ? '+' : ''}${odds} — ${(edge * 100).toFixed(2)}% edge over best book`;
                                if (edge >= 0.25) caretColor = 'green';
                                else if (edge >= 0.15) caretColor = '#F59E0B';
                                else if (edge >= 0.05) caretColor = '#3B82F6';
                                else caretColor = '#9CA3AF';
                              }
                              return (
                                <div
                                  key={book}
                                  className={`expanded-odds-item ${bet.tier}`}
                                  style={{
                                    backgroundImage: `linear-gradient(225deg, ${caretColor} 10px, transparent 10px)`,
                                    backgroundRepeat: 'no-repeat',
                                  }}
                                  title={tooltip}
                                >
                                  <img src={`/logos/${book}.png`} alt={book} />
                                  <span>{odds > 0 ? `+${odds}` : odds}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                    );
                  })()}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default BestBetsTakeover;
